import { Component, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SpotComponent } from '../shared/spot/spot.component';
import { RevealDirective } from '../shared/reveal.directive';
import { DoodleComponent, DoodleKind } from '../shared/doodle/doodle.component';
import {hasStoredToken} from '../../core/services/session-token';

interface Feature {
  title: string;
  body: string;
}

/** One stage of writing a book, in the same order as "How it works" on the home page. */
interface Stage {
  id: string;
  number: string;
  title: string;
  intro: string;
  features: Feature[];
  spark: string;
  /** A small drawing beside the stage number. */
  doodle: DoodleKind;
}

@Component({
    selector: 'app-features',
    templateUrl: './features.component.html',
    styleUrls: ['./features.component.scss'],
    imports: [RouterLink, SpotComponent, RevealDirective, DoodleComponent]
})
export class FeaturesComponent implements OnInit {

  readonly stages: Stage[] = [
    {
      id: 'start',
      doodle: 'sprout',
      number: '01',
      title: 'Start anywhere',
      intro: 'Begin with whatever you have: a genre, a plot, a cast of characters, or only a mood.',
      features: [
        {
          title: 'Genre and style',
          body: 'Blend genres and set the tone, point of view and dialogue style. Every chapter follows it.'
        },
        {
          title: 'Writing instructions',
          body: 'Tell the AI how to write in plain words, the way you would brief a co-author.'
        },
        {
          title: 'A persona to write as',
          body: 'Describe an author for the AI to channel, and keep that voice across the whole book.'
        },
        {
          title: 'Any language',
          body: 'Write your story in the language of your choice, and give your instructions in your own.'
        }
      ],
      spark: 'A sci-fi western with a streak of Gothic romance. Where does it start?'
    },
    {
      id: 'world',
      doodle: 'island',
      number: '02',
      title: 'Build your world',
      intro: 'Give the story something to stand on. Everything you add here stays in view while you write.',
      features: [
        {
          title: 'Characters with inner lives',
          body: 'Background, personality, appearance, relationships and goals. Base them on people you know, or start from a Myers-Briggs type.'
        },
        {
          title: 'Worldbuilding notes',
          body: 'Histories, places, cultures and the rules of your magic, kept beside the story so the AI uses them.'
        },
        {
          title: 'Research at your elbow',
          body: 'Paste in reference material, or switch on web research when the facts need to be current and correct.'
        },
        {
          title: 'An outline that bends',
          body: 'Plan in stages, summarise every chapter, and drag chapter beats into a new order when a scene wants to move.'
        }
      ],
      spark: 'Magic is a finite resource, and it is running out. What does that do to a city?'
    },
    {
      id: 'write',
      doodle: 'quill',
      number: '03',
      title: 'Write together',
      intro: 'Draft chapter by chapter, then shape every line until it sounds like you.',
      features: [
        {
          title: 'Chapters in your voice',
          body: 'Each chapter is written with your characters, your world and the chapters before it in mind.'
        },
        {
          title: 'Rewrite any passage',
          body: 'Select a sentence or a scene and say what should change: colder, shorter, more afraid.'
        },
        {
          title: 'Insert at the cursor',
          body: 'Continue a scene, bridge a gap or add a line of dialogue exactly where you need it.'
        },
        {
          title: 'Pick your pen',
          body: 'Switch models at any time: the most literary prose, the longest memory, or simply the lowest cost.'
        },
        {
          title: 'An assistant who has read it all',
          body: 'Chat about your story to brainstorm, untangle a plot or adjust a character. It knows your project as well as you do.'
        },
        {
          title: 'Stories that go on',
          body: 'Continue a story you love, write the ending it should have had, or give a side character a book of their own.'
        }
      ],
      spark: 'Your quietest friend, dropped into a high-stakes thriller. What do they do first?'
    },
    {
      id: 'share',
      doodle: 'bird',
      number: '04',
      title: 'Share it',
      intro: 'A finished book deserves readers. Send it to a friend, or put it on the shelf for everyone.',
      features: [
        {
          title: 'A cover',
          body: 'Generate a cover image that fits the book.'
        },
        {
          title: 'Download as PDF',
          body: 'Take the whole book with you, ready to print or read anywhere.'
        },
        {
          title: 'A read-only link',
          body: 'Share your book with the people you choose, without giving them the keys.'
        },
        {
          title: 'Publish to Discover',
          body: 'Put it on the public shelf, where other readers can find it, read it and like it.'
        }
      ],
      spark: 'What happened after "happily ever after"? Write the sequel nobody asked for.'
    }
  ];

  userIsLoggedIn = false;

  ngOnInit() {
    this.userIsLoggedIn = hasStoredToken();
  }
}
